import type { LucideIcon } from 'lucide-react';
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';
import type { ReactNode } from 'react';
import { cssVars, cx } from '@/lib/cx';
import s from './KpiTile.module.css';
import { Skeleton } from './Skeleton';

export interface KpiDelta {
  /** Signed change, already formatted (e.g. "+4.2%"). */
  label: string;
  direction: 'up' | 'down' | 'flat';
  /** Whether this direction is good news (a lower harsh-braking rate is good). */
  sentiment?: 'good' | 'bad' | 'neutral';
}

/**
 * A row of KPI tiles: as many columns as fit, each at least `min` wide (auto-fit, minmax), so a
 * value keeps its unit on one line ("25.3 L/100 km"); two per row on a phone. Tiles in a row
 * share their label and figure lines (subgrid), so a label on two lines does not push one
 * tile's number below its neighbours'.
 */
export function KpiTiles({ min = '11.5rem', className, children }: { min?: string; className?: string; children: ReactNode }) {
  return (
    <div className={cx(s.tiles, className)} style={cssVars({ '--kpi-min': min })}>
      {children}
    </div>
  );
}

/**
 * A big number with its unit, an optional delta and a sparkline slot. The value and its unit
 * never break inside; in a narrow tile the unit moves under the value as a whole.
 */
export function KpiTile({
  label,
  value,
  unit,
  delta,
  sparkline,
  footnote,
  icon: Icon,
  loading,
  href,
  className,
}: {
  label: ReactNode;
  value: ReactNode;
  unit?: ReactNode;
  delta?: KpiDelta;
  sparkline?: ReactNode;
  footnote?: ReactNode;
  icon?: LucideIcon;
  loading?: boolean;
  href?: ReactNode;
  className?: string;
}) {
  const DeltaIcon = delta?.direction === 'up' ? ArrowUpRight : delta?.direction === 'down' ? ArrowDownRight : Minus;
  return (
    <div className={cx(s.tile, className)}>
      <div className={s.top}>
        <span className={s.label}>{label}</span>
        {Icon && <Icon className={s.icon} aria-hidden />}
      </div>
      <div className={s.figure}>
        {loading ? (
          <Skeleton width="96px" height="32px" />
        ) : (
          <>
            <span className={s.value}>{value}</span>
            {unit && <span className={s.unit}>{unit}</span>}
          </>
        )}
      </div>
      <div className={s.rest}>
        {(delta || sparkline) && !loading && (
          <div className={s.bottom}>
            {delta && (
              <span className={cx(s.delta, s[delta.sentiment ?? 'neutral'])}>
                <DeltaIcon aria-hidden />
                {delta.label}
              </span>
            )}
            {sparkline && <div className={s.spark}>{sparkline}</div>}
          </div>
        )}
        {footnote && <div className={s.footnote}>{footnote}</div>}
        {href && <div className={s.link}>{href}</div>}
      </div>
    </div>
  );
}
